import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
} from "../../shared/ui";
import type { EmployeeSummary, PageResponse, Priority } from "../../shared/types/api";
import { PRIORITY_LABELS, PRIORITY_ORDER } from "../workitems/workItemVocabulary";


/**
 * §47's bulk update: one change across many items.
 *
 * <p>Every field is "leave alone" by default rather than pre-filled from the first selected item.
 * A form that arrived populated would apply those values to everything the moment it was saved,
 * and the person who only meant to change the priority would silently reassign forty items.
 *
 * <p>Each change still writes its own history entry, exactly as a single edit would. Bulk edits
 * are precisely when somebody later asks why forty things changed at once.
 */
export function BulkUpdateDrawer({
  workItemIds,
  open,
  onClose,
  onSaved,
}: {
  workItemIds: string[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [priority, setPriority] = useState<Priority | "">("");
  const [assigneeId, setAssigneeId] = useState("");
  const [storyPoints, setStoryPoints] = useState("");
  const [labels, setLabels] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setPriority("");
      setAssigneeId("");
      setStoryPoints("");
      setLabels("");
      setError(null);
    }
  }, [open]);

  const parsedLabels = labels
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);

  const nothingToDo =
    priority === "" && assigneeId === "" && storyPoints === "" && parsedLabels.length === 0;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/v1/backlog/bulk-update", {
        workItemIds,
        priority: priority || null,
        assigneeId: assigneeId || null,
        storyPoints: storyPoints === "" ? null : Number(storyPoints),
        addLabels: parsedLabels.length > 0 ? parsedLabels : null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not apply those changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Edit ${workItemIds.length} items`}
      subtitle="Only the fields you fill in are changed."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={nothingToDo}>
            {saving ? "Applying…" : `Apply to ${workItemIds.length}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          Every field starts blank on purpose. Anything left alone stays as it is on each item,
          so changing the priority here will not quietly reassign everything as well.
        </InlineAlert>

        <Field label="Priority">
          <Select
            value={priority}
            onChange={(event) => setPriority(event.target.value as Priority | "")}
          >
            <option value="">Leave as is</option>
            {PRIORITY_ORDER.map((value) => (
              <option key={value} value={value}>
                {PRIORITY_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Assignee" hint="One owner per item (§21) — this sets the same one on each.">
          <Select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
            <option value="">Leave as is</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Story points"
          hint="Sets the same size on every selected item, so use it for genuinely similar work."
        >
          <Input
            type="number"
            min={0}
            placeholder="Leave as is"
            value={storyPoints}
            onChange={(event) => setStoryPoints(event.target.value)}
          />
        </Field>

        <Field label="Add labels" hint="Comma separated. Existing labels are kept.">
          <Input
            placeholder="security, mobile"
            value={labels}
            onChange={(event) => setLabels(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
